import React from 'react';
import _ from 'underscore';

import {BaseMigrationObjectPlan} from 'config-migration/MigrationConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

import {ObjectAction, ObjectActionType} from '../types/objectActions';

interface ActionDropdownProps {
  object: BaseMigrationObjectPlan;
  onUpdateObject: (object: BaseMigrationObjectPlan) => void;
  excludeIgnore?: boolean;
  showActionInline?: boolean;
}

export function getActionDescription(action: ObjectAction): JSX.Element | string {
  if (action.type === ObjectActionType.CREATE) {
    return 'Create';
  }
  if (action.type === ObjectActionType.IGNORE) {
    return 'Ignore';
  } else {
    return (
      <span>
        Update <strong>{action.target.displayName}</strong>
        {action.target.archived && ` (Archived)`}
      </span>
    );
  }
}

export function getOptionValue(action: ObjectAction): string {
  return action.type === ObjectActionType.UPDATE ? action.target.apiIdentifier : action.type;
}

export function getActionOption(action: ObjectAction, onSelect: (action: ObjectAction) => void): JSX.Element {
  return (
    <Option
      value={getOptionValue(action)}
      onSelect={() => {
        onSelect(action);
      }}
      key={_.uniqueId('action-')}
    >
      {getActionDescription(action)}
    </Option>
  );
}

export default function ObjectActionDropdown(props: ActionDropdownProps): JSX.Element {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const updateObject = (selectedAction: ObjectAction) => {
    props.onUpdateObject({...props.object, selectedAction});
  };
  if (props.showActionInline) {
    return (
      <div className="u-mini-spacing-right u-flex-grow">
        {getActionDescription(props.object.selectedAction)}
      </div>
    );
  }
  return (
    <SelectDropdown
      className="u-mini-spacing-right u-flex-grow"
      menu={
        <Menu
          height={Menu.HEIGHTS.MEDIUM}
          width={Menu.WIDTHS.FULL}
          value={getOptionValue(props.object.selectedAction)}
          scrollable={true}
        >
          <Section>
            {getActionOption(
              props.object.hasProvenanceUriMatch
                ? props.object.initialAction
                : {type: ObjectActionType.CREATE},
              updateObject
            )}
            {!props.excludeIgnore && getActionOption({type: ObjectActionType.IGNORE}, updateObject)}
          </Section>
          <Section>
            {!props.object.hasProvenanceUriMatch &&
              props.object.additionalActions.map((action) => getActionOption(action, updateObject))}
          </Section>
        </Menu>
      }
      displayName={getActionDescription(props.object.selectedAction)}
    />
  );
}
